import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';

import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { ToastProvider } from '@coursera/cds-core';

import { AccountSettingsContextSwitcherGateProvider } from 'bundles/account-settings/contexts/AccountSettingsContextSwitcherGateContext';
import AccountSettingsOrgEnrollmentsQuery from 'bundles/account-settings/queries/AccountSettingsOrgEnrollmentsQuery.graphql';
import type {
  AccountSettingsOrgEnrollmentsQuery as AccountSettingsOrgEnrollmentsQueryType,
  AccountSettingsOrgEnrollmentsQueryVariables,
} from 'bundles/account-settings/queries/__generated__/AccountSettingsOrgEnrollmentsQuery';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import ConsumerMyPageHeaderContext from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import type { ConsumerPageHeader_MyPageHeaderFragment as ConsumerPageHeaderFragment } from 'bundles/composable-page-header-consumer/queries/__generated__/ConsumerMyPageHeaderQuery';
import {
  type UpdateUserExperienceInput,
  UserExperienceContext,
  type UserExperienceSelection,
} from 'bundles/unified-common/contexts/UserExperienceContext';
import GetUserExperienceContextQuery from 'bundles/unified-common/queries/GetUserExperienceContextQuery.graphql';
import UpdateUserExperienceContextMutation from 'bundles/unified-common/queries/UpdateUserExperienceContextMutation.graphql';
import type { GetUserExperienceContextQuery as GetUserExperienceContextQueryType } from 'bundles/unified-common/queries/__generated__/GetUserExperienceContextQuery';
import type {
  UpdateUserExperienceContextMutation as UpdateUserExperienceContextMutationType,
  UpdateUserExperienceContextMutationVariables,
} from 'bundles/unified-common/queries/__generated__/UpdateUserExperienceContextMutation';

function buildMutationInput(input: UpdateUserExperienceInput): UpdateUserExperienceContextMutationVariables['input'] {
  const { selection, organizationId } = input;
  if (selection === 'consumer') {
    return { selection: { consumer: {} } };
  }
  if (selection === 'enterprise' && organizationId) {
    return { selection: { enterprise: { organizationId } } };
  }
  return { selection: { consumer: {} } };
}

/** Fetches user experience for account-settings (e.g. Switch Account). */
const AccountSettingsUserExperienceProvider = ({ children }: { children: React.ReactNode }) => {
  const skip = !isAuthenticatedUser();

  const { data, loading, error } = useQuery<GetUserExperienceContextQueryType>(GetUserExperienceContextQuery, {
    context: { clientName: 'gatewayGql' },
    skip,
    errorPolicy: 'all',
    onError: (errorObj) => {
      captureException(errorObj, {
        extra: { message: 'Error fetching user experience context in account-settings' },
      });
    },
  });

  const storedExperience: UserExperienceSelection = data?.UserExperienceContext?.getUserExperienceContext ?? null;

  const [updateMutation, { loading: isUpdating, error: mutationError }] = useMutation<
    UpdateUserExperienceContextMutationType,
    UpdateUserExperienceContextMutationVariables
  >(UpdateUserExperienceContextMutation, {
    context: { clientName: 'gatewayGql' },
    refetchQueries: [{ query: GetUserExperienceContextQuery }],
    awaitRefetchQueries: true,
  });

  const updateUserExperience = useCallback(
    async (input: UpdateUserExperienceInput): Promise<UserExperienceSelection> => {
      const result = await updateMutation({
        variables: { input: buildMutationInput(input) },
      });

      const payload = result.data?.UserExperienceContext_UpdateUserExperienceContext;
      if (payload?.__typename === 'UserExperienceContext_UpdateUserExperienceContextResultSuccess') {
        return payload.selection;
      }
      return null;
    },
    [updateMutation]
  );

  const value = useMemo(
    () => ({
      userExperience: storedExperience,
      isLoading: loading,
      error: error ?? mutationError ?? null,
      querySkipped: false,
      updateUserExperience,
      isUpdating,
    }),
    [storedExperience, loading, error, mutationError, updateUserExperience, isUpdating]
  );

  return <UserExperienceContext.Provider value={value}>{children}</UserExperienceContext.Provider>;
};

/**
 * Fetches org enrollments only (minimal query for Switch Account). Body can use
 * useConsumerMyPageHeader() for naptimeOrganizationsWithProgramEnrollments.
 */
const AccountSettingsMyPageHeaderProvider = ({ children }: { children: React.ReactNode }) => {
  const isLoggedIn = !!userGet()?.id;
  const { data, error } = useQuery<AccountSettingsOrgEnrollmentsQueryType, AccountSettingsOrgEnrollmentsQueryVariables>(
    AccountSettingsOrgEnrollmentsQuery,
    {
      context: { clientName: 'gatewayGql' },
      errorPolicy: 'all',
      skip: !isLoggedIn,
    }
  );

  const queryResult = data?.PageHeader?.queryMyPageHeader;
  const myPageHeader = queryResult
    ? ({
        naptimeOrganizationsWithProgramEnrollments: queryResult.naptimeOrganizationsWithProgramEnrollments ?? [],
      } as ConsumerPageHeaderFragment)
    : undefined;

  return (
    <ConsumerMyPageHeaderContext.Provider value={{ myPageHeader, error }}>
      <AccountSettingsContextSwitcherGateProvider>{children}</AccountSettingsContextSwitcherGateProvider>
    </ConsumerMyPageHeaderContext.Provider>
  );
};

/**
 * Provides user experience + org enrollments for the account-settings page body.
 * Wrapped in an error boundary so provider render errors are caught and logged; nothing is displayed on error.
 */
const AccountSettingsPageProviders = ({ children }: { children: React.ReactNode }) => {
  return (
    <ErrorBoundaryWithTagging componentName="AccountSettingsPageProviders">
      <AccountSettingsMyPageHeaderProvider>
        <AccountSettingsUserExperienceProvider>
          <ToastProvider position="bottom-right">{children}</ToastProvider>
        </AccountSettingsUserExperienceProvider>
      </AccountSettingsMyPageHeaderProvider>
    </ErrorBoundaryWithTagging>
  );
};

export default AccountSettingsPageProviders;
